import { useTranslation } from 'react-i18next';

import { ErrorDisplay } from '../../../../Shared/components/ErrorDisplay/ErrorDisplay';
import { ApiError } from '../../../../Shared/interfaces/ApiError';
import CustomSpinner from '../../../../UiComponentsLibrary/CustomSpinner';
import {
  BookingStep,
  DetailedFormType,
} from '../constants/DetailedBookingConstants';
import { ContextualNode, BookingFormData } from '../interface/BookingFormInterface';
import { BookingMode, SlotEntryData, SlotPackage } from '../interface/DetailedBookingInterface';
import CreateBookingForm from './CreateBookingForm/CreateBookingForm';
import MultiSlotDetailedForm from './MultiSlotDetailedForm';

interface CurrentFormRendererProps {
  isCreatingBooking: boolean;
  handleFormSubmit: (formData: BookingFormData) => Promise<void>;
  handleAllSlotsSubmit: (allSlotEntries: SlotEntryData[]) => Promise<void>;
  validateFormTrigger: boolean;
  validateDetailedFormTrigger: boolean;
  bookingFormData: BookingFormData | null;
  bookingError: ApiError | null;
  bookingErrorCustomMessage?: string;
  currentStep: BookingStep;
  detailedFormType: DetailedFormType | null;
  bookingId: string | null;
  hasNavigatedToSecondPage: boolean;
  isEditMode: boolean;
  formMode: BookingMode;
  existingContextualNodes: ContextualNode[] | null;
  entryId: number;
  editBookingId?: string | null;
  resetDetailedFormErrors?: boolean;
  setResetDetailedFormErrors?: (value: boolean) => void;
  filteredSlotPackages?: SlotPackage[] | null;
}

const CurrentFormRenderer = ({
  isCreatingBooking,
  handleFormSubmit,
  handleAllSlotsSubmit,
  validateFormTrigger,
  validateDetailedFormTrigger,
  bookingFormData,
  bookingError,
  bookingErrorCustomMessage,
  currentStep,
  detailedFormType,
  bookingId,
  entryId,
  editBookingId,
  hasNavigatedToSecondPage,
  isEditMode,
  formMode,
  existingContextualNodes,
  resetDetailedFormErrors,
  setResetDetailedFormErrors,
  filteredSlotPackages,
}: CurrentFormRendererProps) => {
  const { t } = useTranslation();
  const { EDIT: EDITMODE, CREATE: CREATEMODE } = BookingMode;
  if (isCreatingBooking) return <CustomSpinner />;

  if (bookingError)
    return (
      <ErrorDisplay
        error={bookingError}
        customMessage={bookingErrorCustomMessage}
      />
    );

  if (currentStep === BookingStep.INITIAL) {
    const formKey =
      formMode === EDITMODE && editBookingId
        ? `edit-${editBookingId}`
        : formMode === CREATEMODE && hasNavigatedToSecondPage && isEditMode
          ? `edit-internal-${bookingId || 'unknown'}`
          : `create-new`;

    const shouldBeInEditMode =
      formMode === EDITMODE || (hasNavigatedToSecondPage && isEditMode);

    const safeInitialData = shouldBeInEditMode
      ? bookingFormData || undefined
      : undefined;

    if (shouldBeInEditMode && !bookingFormData) return <CustomSpinner />;

    return (
      <CreateBookingForm
        key={formKey}
        initialData={safeInitialData}
        onFormSubmit={handleFormSubmit}
        validateFormTrigger={validateFormTrigger}
        isEditMode={isEditMode}
      />
    );
  }

  if (currentStep === BookingStep.DETAILED && bookingFormData && detailedFormType)
    return (
      <MultiSlotDetailedForm
        initialData={bookingFormData}
        bookingId={bookingId}
        entryId={entryId || 0}
        detailedFormType={detailedFormType}
        existingContextualNodes={existingContextualNodes}
        resetDetailedFormErrors={resetDetailedFormErrors}
        setResetDetailedFormErrors={setResetDetailedFormErrors}
        filteredSlotPackages={filteredSlotPackages}
        validateFormTrigger={validateDetailedFormTrigger}
        onAllSlotsSubmit={handleAllSlotsSubmit}
        isEditMode={isEditMode}
      />
    );

  return <div id="no-form-data-message">{t('no-form-data-message')}</div>;
};

export default CurrentFormRenderer;
